import { Calendar } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';

function renderCalendar(container) {
	const feeds = JSON.parse(container.dataset.eventFeeds);
	const requestedFeed = new URLSearchParams(window.location.search).get('calendar_feed');
	const initialFeed = feeds.some(feed => feed.id === requestedFeed) ? requestedFeed : null;
	const toolbar = document.createElement('div');
	toolbar.className = 'wm-calendar-toolbar';
	const navigation = document.createElement('div');
	navigation.className = 'wm-calendar-navigation';
	const button = (label, text, handler) => {
		const element = document.createElement('button');
		element.type = 'button';
		element.setAttribute('aria-label', label);
		element.textContent = text;
		element.addEventListener('click', handler);
		return element;
	};
	const title = document.createElement('h2');
	title.className = 'wm-calendar-title';
	title.setAttribute('aria-live', 'polite');
	const view = document.createElement('select');
	view.className = 'wm-calendar-view';
	view.setAttribute('aria-label', 'Calendar view');
	view.add(new Option('Month', 'dayGridMonth'));
	view.add(new Option('Week', 'dayGridWeek'));
	const filters = document.createElement('div');
	filters.className = 'wm-calendar-filters';
	filters.setAttribute('aria-label', 'Calendar feeds');
	const grid = document.createElement('div');
	grid.className = 'wm-calendar-grid';
	const loadingStatus = document.createElement('p');
	loadingStatus.className = 'wm-calendar-loading';
	loadingStatus.setAttribute('role', 'status');
	loadingStatus.textContent = 'Loading calendar...';
	loadingStatus.hidden = true;
	const error = document.createElement('p');
	error.className = 'wm-calendar-error';
	error.setAttribute('role', 'alert');
	error.hidden = true;
	const failedFeeds = new Map();
	const updateError = () => {
		error.replaceChildren();
		error.hidden = failedFeeds.size === 0;
		if (error.hidden) return;
		error.append(document.createTextNode(`Calendar feeds could not be loaded: ${Array.from(failedFeeds.values()).join(', ')}. `));
		error.append(button('Retry loading calendar feeds', 'Try again', () => {
			Array.from(failedFeeds.keys()).forEach(id => calendar.getEventSourceById(id)?.refetch());
		}));
	};
	const popup = document.createElement('div');
	popup.className = 'wm-event-popup';
	popup.setAttribute('role', 'dialog');
	popup.setAttribute('aria-label', 'Event details');
	let popupTrigger;
	const closePopup = (restoreFocus = false) => {
		popup.classList.remove('open');
		if (restoreFocus && popupTrigger?.isConnected) popupTrigger.focus();
	};
	const courseSegments = new Map();
	const updateCourseProgress = () => {
		const today = new Date();
		today.setHours(0, 0, 0, 0);
		const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
		courseSegments.forEach((event, element) => {
			const end = event.end || event.start;
			const ongoing = event.start < today && end > today;
			const cells = Array.from(element.closest('tr')?.querySelectorAll('td[data-date]') || []);
			const firstUnelapsed = cells.find(cell => cell.dataset.date >= todayKey);
			const bounds = element.getBoundingClientRect();
			const cutoff = firstUnelapsed ? firstUnelapsed.getBoundingClientRect().left : bounds.right;
			const width = ongoing ? Math.max(0, Math.min(bounds.width, cutoff - bounds.left)) : 0;
			element.style.setProperty('--course-progress-width', `${width}px`);
			element.classList.toggle('wm-calendar-course--ongoing', ongoing && width > 0);
		});
	};
	const calendar = new Calendar(grid, {
		plugins: [dayGridPlugin],
		initialView: 'dayGridMonth',
		firstDay: 1,
		weekends: true,
		height: 'auto',
		headerToolbar: false,
		fixedWeekCount: false,
		dayHeaderContent: info => window.matchMedia('(max-width: 767.98px)').matches
			? ['Su', 'M', 'T', 'W', 'Th', 'F', 'Sa'][info.date.getDay()]
			: info.text,
		eventDisplay: 'block',
		eventClassNames: info => {
			const today = new Date();
			today.setHours(0, 0, 0, 0);
			const end = info.event.end || info.event.start;
			const past = info.event.allDay ? end <= today : end < new Date();
			return past ? ['wm-calendar-event--past'] : [];
		},
		displayEventTime: false,
		windowResize: updateCourseProgress,
		eventsSet: () => requestAnimationFrame(updateCourseProgress),
		loading: loading => {
			container.setAttribute('aria-busy', String(loading));
			loadingStatus.hidden = !loading;
			navigation.querySelectorAll('button, select').forEach(control => { control.disabled = loading; });
			filters.querySelectorAll('button').forEach(control => { control.disabled = loading; });
			error.querySelectorAll('button').forEach(control => { control.disabled = loading; });
		},
		datesSet: info => {
			title.textContent = info.view.title;
			view.value = info.view.type;
			closePopup();
			if (Array.isArray(window.dataLayer)) {
				window.dataLayer.push({ event: 'wxc.monthLoad', 'wxc.month': info.view.title });
			}
		},
		eventContent: info => {
			const label = document.createElement('span');
			label.className = 'wm-calendar-event-title';
			label.textContent = info.event.title;
			const nodes = [label];
			return { domNodes: nodes };
		},
		eventDidMount: info => {
			if (info.event.source?.id === 'product') {
				courseSegments.set(info.el, info.event);
				requestAnimationFrame(updateCourseProgress);
			}
			info.el.setAttribute('tabindex', '0');
			info.el.setAttribute('role', 'button');
			info.el.setAttribute('aria-label', info.event.title);
			info.el.classList.toggle('wm-calendar-single-day', info.event.allDay && info.event.end - info.event.start <= 86400000);
			info.el.addEventListener('keydown', event => {
				if (event.key === 'Enter' || event.key === ' ') {
					event.preventDefault();
					info.el.click();
				}
			});
		},
		eventWillUnmount: info => courseSegments.delete(info.el),
		eventClick: info => {
			info.jsEvent.preventDefault();
			popupTrigger = info.el;
			popup.replaceChildren();
			const feed = feeds.find(item => item.id === info.event.source?.id);
			popup.dataset.feed = feed?.id || '';
			popup.style.setProperty('--popup-background', feed?.backgroundColor || '#ffffff');
			popup.style.setProperty('--popup-text', feed?.textColor || '#003f29');
			const close = button('Close event details', '\u00d7', () => closePopup(true));
			close.className = 'wm-close-button';
			const details = info.event.extendedProps.details;
			const badge = document.createElement('span');
			badge.className = 'wm-event-popup-label';
			badge.textContent = details?.label || feed?.label || '';
			const heading = document.createElement('h3');
			heading.textContent = info.event.title;
			const content = document.createElement('div');
			content.className = 'wm-event-popup-content';
			if (details) {
				const metadata = document.createElement('div');
				metadata.className = 'wm-event-popup-meta';
				[['date', 'calendar'], ['time', 'clock'], ['location', 'map-pin']].forEach(([key, icon]) => {
					if (!details[key]) return;
					const row = document.createElement('div');
					const symbol = document.createElement('span');
					symbol.className = `icon icon--${icon}`;
					symbol.setAttribute('aria-hidden', 'true');
					row.append(symbol, document.createTextNode(details[key]));
					metadata.append(row);
				});
				content.append(metadata);
				if (details.summary) {
					const description = document.createElement('p');
					description.className = 'wm-event-popup-summary';
					description.textContent = details.summary;
					content.append(description);
				}
			} else {
				content.innerHTML = `${info.event.extendedProps.header || ''}${info.event.extendedProps.description || ''}`;
			}
			const actions = document.createElement('div');
			actions.className = 'wm-event-popup-actions';
			actions.innerHTML = info.event.extendedProps.buttons || '';
			actions.querySelectorAll('a').forEach(link => {
				link.classList.add('btn', 'btn-primary');
				const arrow = document.createElement('span');
				arrow.className = 'icon icon--arrow-right';
				arrow.setAttribute('aria-hidden', 'true');
				link.replaceChildren(arrow, document.createTextNode('Read more'));
			});
			popup.append(close, badge, heading, content, actions);
			popup.classList.add('open');
			const bounds = container.getBoundingClientRect();
			const event = info.el.getBoundingClientRect();
			const left = Math.max(8 - bounds.left, Math.min(event.left - bounds.left, window.innerWidth - bounds.left - popup.offsetWidth - 8));
			const desiredTop = event.bottom + 8;
			const top = Math.max(8, Math.min(desiredTop, window.innerHeight - popup.offsetHeight - 8));
			popup.style.left = `${left}px`;
			popup.style.top = `${top - bounds.top}px`;
			close.focus({ preventScroll: true });
		},
	});
	const previous = button('Previous period', '', () => calendar.prev());
	const next = button('Next period', '', () => calendar.next());
	const chevron = '<svg width="23" height="23" viewBox="0 0 23 23" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"><path d="M13.9783 11.5003L8.95375 16.5591C8.57956 16.9365 8.57955 17.5483 8.95375 17.9256C9.32795 18.303 9.93464 18.303 10.3088 17.9256L16.0108 12.1837C16.3851 11.8063 16.3851 11.1944 16.0108 10.817L10.3088 5.075C9.93464 4.69765 9.32795 4.69765 8.95375 5.075C8.57955 5.45235 8.57955 6.06416 8.95375 6.44151L13.9783 11.5003Z" fill="currentColor"/></svg>';
	previous.innerHTML = chevron;
	previous.firstElementChild.style.transform = 'rotate(180deg)';
	next.innerHTML = chevron;
	navigation.append(previous, next, title, view);
	view.addEventListener('change', () => calendar.changeView(view.value));
	const sources = feeds.map(feed => ({
		id: feed.id,
		url: feed.url,
		color: feed.backgroundColor,
		borderColor: feed.accentColor,
		textColor: feed.textColor,
		extraParams: () => ({ language: container.dataset.language, period: container.dataset.period || '' }),
		success: events => {
			failedFeeds.delete(feed.id);
			updateError();
			return events;
		},
		failure: failure => {
			const status = failure.response?.status;
			failedFeeds.set(feed.id, `${feed.label}${status ? ` (HTTP ${status})` : ''}`);
			updateError();
		},
	}));
	feeds.forEach((feed, index) => {
		const toggle = button(`Toggle ${feed.label}`, feed.label, () => {
			const active = toggle.getAttribute('aria-pressed') === 'true';
			const source = calendar.getEventSourceById(feed.id);
			if (active && source) source.remove();
			if (!active) calendar.addEventSource(sources[index]);
			if (active) {
				failedFeeds.delete(feed.id);
				updateError();
			}
			toggle.setAttribute('aria-pressed', String(!active));
			closePopup();
		});
		toggle.className = `wm-calendar-feed wm-calendar-feed--${feed.id}`;
		toggle.setAttribute('aria-pressed', String(!initialFeed || feed.id === initialFeed));
		toggle.style.setProperty('--feed-background', feed.accentColor);
		toggle.style.setProperty('--feed-text', feed.textColor);
		filters.append(toggle);
	});
	toolbar.append(navigation, filters);
	container.append(toolbar, loadingStatus, error, grid, popup);
	calendar.render();
	const progressObserver = new ResizeObserver(() => requestAnimationFrame(updateCourseProgress));
	progressObserver.observe(grid);
	sources.filter(source => !initialFeed || source.id === initialFeed).forEach(source => calendar.addEventSource(source));
	container.addEventListener('keydown', event => {
		if (event.key === 'Escape') closePopup(true);
	});
	document.addEventListener('click', event => {
		if (!popup.contains(event.target) && !event.target.closest('.fc-event')) closePopup();
	});
	window.addEventListener('resize', () => closePopup());
}

document.querySelectorAll('.wm-calendar').forEach(renderCalendar);
